<template>
    <div class="home">
        <page-hero title="Commercial League" eyebrow="Basketball" subtitle="Αποτελέσματα, βαθμολογίες και στατιστικά των ομάδων του Commercial League">
            <template v-slot:aside>
                <!-- shortcuts to other pages: alike, with an arrow, so neither reads as "selected" -->
                <div class="home__actions">
                    <router-link :to="{ name: 'News' }" class="home__shortcut">
                        <v-icon small class="home__shortcut-icon">mdi-newspaper-variant-outline</v-icon>
                        Τελευταία νέα
                        <v-icon small class="home__shortcut-arrow">mdi-arrow-right</v-icon>
                    </router-link>
                    <router-link :to="{ name: 'Teams' }" class="home__shortcut">
                        <v-icon small class="home__shortcut-icon">mdi-account-group-outline</v-icon>
                        Όλες οι ομάδες
                        <v-icon small class="home__shortcut-arrow">mdi-arrow-right</v-icon>
                    </router-link>
                </div>
            </template>
        </page-hero>

        <div class="cl-page">
            <section class="cl-section">
                <section-heading title="Match Center" icon="mdi-basketball" />
                <match-center :matches="matches" />
            </section>

            <!-- the latest news, as the spotlight of the news page -->
            <section v-if="articles.length" class="cl-section">
                <section-heading title="Νέα" icon="mdi-newspaper-variant-outline">
                    <router-link :to="{ name: 'News' }" class="home__more">Όλα τα νέα<v-icon x-small>mdi-arrow-right</v-icon></router-link>
                </section-heading>
                <news-spotlight :articles="articles" compact />
            </section>

            <section class="cl-section">
                <section-heading title="Standings" icon="mdi-podium" />
                <div class="home__standings">
                    <div v-for="leagueGroup in leagueGroups" :key="`LeagueGroup-${leagueGroup.id}`" class="home__group">
                        <div class="home__group-title">{{ /league$/i.test(leagueGroup.title.trim()) ? leagueGroup.title : leagueGroup.title + ' League' }}</div>
                        <home-standings :league-group="leagueGroup" :leagues="leagues" />
                    </div>
                </div>
            </section>

            <section class="cl-section">
                <section-heading title="Sponsors & Partners" icon="mdi-handshake-outline" />
                <div class="home__strip">
                    <marquee :speed="50" :height="120" :images="sponsors" />
                </div>
            </section>

            <section class="cl-section">
                <section-heading title="Teams" icon="mdi-account-group-outline">
                    <router-link :to="{ name: 'Teams' }" class="home__more">Όλες οι ομάδες <v-icon x-small>mdi-arrow-right</v-icon></router-link>
                </section-heading>
                <div class="home__strip">
                    <marquee v-if="teams" :speed="250" :height="150" :images="teams" />
                </div>
            </section>
        </div>
    </div>
</template>

<script>
    import Marquee from '@/components/Marquee.vue'
    import teamService from '@/services/team'
    import HomeStandings from '@/components/HomeStandings.vue'
    import MatchCenter from '@/components/MatchCenter.vue'
    import NewsSpotlight from '@/components/NewsSpotlight'
    import articleService from '@/services/article'
    import { pollWhileVisible, SCHEDULE_REFRESH_MS } from '@/utils/matchLive'
    import matchService from '@/services/match'
    import PageHero from '@/components/PageHero'
    import SectionHeading from '@/components/SectionHeading'

    export default {
        name: 'Home',
        components: {
            Marquee,
            HomeStandings,
            MatchCenter,
            NewsSpotlight,
            PageHero,
            SectionHeading
        },
        data() {
            return {
                sponsors: [
                    require('@/assets/sportsiq.jpg'),
                    require('@/assets/gsa.jpg'),
                    require('@/assets/molten.png'),
                    require('@/assets/e-academy.png'),
                    require('@/assets/IOLI_logo GR.jpg')
                ],
                teams: null,
                assetsUrl: 'https://storage.googleapis.com',
                matches: [],
                articles: [],
                stopRefresh: null,
                videos: [],
                VUE_APP_YT_API_KEY: process.env.VUE_APP_YT_API_KEY,
                VUE_APP_YT_CHANNEL_ID: process.env.VUE_APP_YT_CHANNEL_ID
            }
        },
        computed: {
            // only the groups that have leagues: an empty group has no standings to show
            leagueGroups() {
                const leagues = this.leagues || []
                return (this.$store.getters.getLeagueGroups || []).filter(group => leagues.some(league => league.leagueGroup && league.leagueGroup.id === group.id))
            },
            leagues() {
                return this.$store.getters.getLeagues
            }
        },
        // mounted() {
        //     this.fetchLiveStreams()
        // },
        created() {
            this.fetchTeams()
            this.initializeHome()
            articleService.getArticles(1, 6).then(resp => {
                this.articles = resp.data.content || []
            }).catch(() => {})
            // new times, postponements and final results without a reload
            this.stopRefresh = pollWhileVisible(() => this.getMatches().catch(() => {}), SCHEDULE_REFRESH_MS)
        },
        beforeDestroy() {
            this.stopRefresh()
        },
        methods: {
            async initializeHome() {
                await Promise.all([
                    this.$store.dispatch('ensureLeagueGroups'),
                    this.$store.dispatch('ensureLeagues'),
                    this.getMatches()
                ])
            },
            playVideo(videoId) {
                // Set the isPlaying flag on the clicked video
                this.videos = this.videos.map(v => ({
                    ...v,
                    isPlaying: v.id.videoId === videoId ? true : v.isPlaying || false
                }))
            },
            decodeHTML(html) {
                const txt = document.createElement('textarea')
                txt.innerHTML = html
                return txt.value
            },
            // async fetchLiveStreams() {
            //     const res = await fetch(
            //         `https://www.googleapis.com/youtube/v3/search?part=snippet&eventType=completed&type=video&order=date&maxResults=10&channelId=${this.VUE_APP_YT_CHANNEL_ID}&key=${this.VUE_APP_YT_API_KEY}`
            //     )
            //     const data = await res.json()
            //     this.videos = data.items
            // },
            getMatches() {
                return matchService.getMatches(1, 100, { query: '{"NotOr": [ {"scoreTeam1": 0, "scoreTeam2": 20}, {"scoreTeam1": 20, "scoreTeam2": 0} ]}' }).then(resp => {
                    this.matches = resp.data.content
                })
            },
            fetchTeams() {
                teamService.getTeams().then(resp => {
                    this.teams = resp.data.content.map(team => {
                        return team.logo ? `${this.assetsUrl}/${team.logo}` : require('@/assets/main_logo.png')
                    })
                })
            }
        }
    }
</script>

<style scoped>
.home__more {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #009688 !important;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.8rem;
    font-weight: 700;
    text-decoration: none;
}

.home__more .v-icon {
    color: inherit !important;
}

.home__more:hover {
    text-decoration: underline;
}

.home__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.home__shortcut {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.14);
    color: #fff !important;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.85rem;
    font-weight: 700;
    text-decoration: none;
    transition: background 0.15s, border-color 0.15s;
}

.home__shortcut-icon {
    color: #03e5b7 !important;
}

.home__shortcut-arrow {
    color: rgba(255, 255, 255, 0.5) !important;
    transition: transform 0.15s, color 0.15s;
}

.home__shortcut:hover {
    background: rgba(3, 229, 183, 0.12);
    border-color: rgba(3, 229, 183, 0.5);
}

.home__shortcut:hover .home__shortcut-arrow {
    color: #03e5b7 !important;
    transform: translateX(3px);
}

.home__standings {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(520px, 100%), 1fr));
    gap: 24px;
}

/* a grid item may shrink below its content (the tables scroll inside) */
.home__group {
    min-width: 0;
}

.home__group-title {
    margin-bottom: 8px;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.4rem;
    color: #232323;
    text-transform: uppercase;
}

.home__strip {
    padding: 10px 0;
    border-radius: 10px;
    background: #fff;
    box-shadow: 0 4px 16px rgba(35, 35, 35, 0.08);
    overflow: hidden;
}

@media (max-width: 700px) {
    .home__standings {
        grid-template-columns: 1fr;
    }
}
</style>
